
import gql from "graphql-tag";
import ColorSelectionMenu from "@/components/ColorSelectionMenu.vue";
import {
	GET_ANY_ITEM_BY_ID,
	PACKAGE_CATEGORY_QUERY,
	SUB_ITEM_CATEGORY_QUERY,
	SEARCH_INVENTORY_LISTINGS_QUERY,
	SEARCH_INVENTORY_LISTINGS_BY_SYSTEMCATEGORYID_QUERY,
} from "@/gql/inventory";
import SizingChart from "@/components/SizingChart.vue";
import {
	LineItem,
	LineSubItem,
	ColorOption,
	hasValidColorSelections,
	InventoryItem,
} from "../../DataTypes";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { validateSizing } from "@/helpers/validator";
import { getItemPrice, renderPrice } from "@/helpers/PricingCalculator";
import { splitColorMenuSelections } from "@/helpers/selectionHelper";

export default {
	name: "SalesEntryTable",
	props: {
		loading: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Array,
			default() {
				return [];
			},
		},
		maxHeight: {
			type: Number,
			default: 350,
		},
		// A locked quote (ordered or cancelled) is a historical record (#1475): no adding or
		// removing lines and no typing into cells, while scrolling, Expand and the
		// view buttons keep working.
		locked: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		SizingChart,
		ColorSelectionMenu,
		SystemCategoryIcon,
	},
	data(): {
		localTableData: LineItem[];
		showItemSearchByLine: number | null | "#";
		itemSearchResults: any[];
		showItemNotesByLine: number | null | "#";
		showItemColorsByLine: number | null | "#";
		showItemSizingByLine: number | null | "#";
		focusedItemIndex: number;
		itemIndex: number;
		childIndex: number | null | undefined;
		subChildIndex: number | null | undefined;
		preventCloseOnBlur: Boolean;
		quantityState: any[];
		uniqueIdCounter: number;
		expanded: Boolean;
		hasOverflow: Boolean;
		resizeObserver: ResizeObserver | null;
		hideSearchTimeout: number | null;
		itemUndoStack: any[];
		itemRedoStack: any[];
		pendingUndoSnapshot: any;
		handleUndoRedo: ((e: KeyboardEvent) => void) | null;
	} {
		return {
			localTableData: [],
			showItemSearchByLine: null,
			itemSearchResults: [],
			showItemNotesByLine: null,
			showItemColorsByLine: null,
			showItemSizingByLine: null,
			focusedItemIndex: -1,
			itemIndex: 0,
			childIndex: null,
			subChildIndex: null,
			preventCloseOnBlur: false,
			quantityState: [],
			uniqueIdCounter: 0,
			expanded: false,
			hasOverflow: false,
			resizeObserver: null,
			hideSearchTimeout: null,
			itemUndoStack: [],
			itemRedoStack: [],
			pendingUndoSnapshot: null,
			handleUndoRedo: null,
		};
	},
	methods: {
		/**
		 * Locked quotes (#1475): fields in the table accept no input. Runs in the capture phase on
		 * the table root, so stopping propagation keeps the event from ever reaching the field's own
		 * handlers (search on keyup, Enter/arrow selection, number stepping). Tab and Escape still
		 * move focus so the record stays keyboard-navigable.
		 */
		blockLockedInput(event) {
			if (!this.locked) return;
			const tag = (event.target && event.target.tagName) || "";
			if (tag !== "INPUT" && tag !== "TEXTAREA") return;
			if ((event.type === "keydown" || event.type === "keyup") && (event.key === "Tab" || event.key === "Escape")) return;
			event.stopPropagation();
			event.preventDefault();
		},
		log(thing: any) {
			if (thing) {
				console.log(thing);
			}
		},

		toNumber(val: string | number | null | undefined): number {
			return (typeof val === "string" ? parseInt(val) : val) as number;
		},

		colorSelections(item: LineItem): any {
			return item.colorSelections as any;
		},

		colorOptions(item: LineItem): any {
			// Check if colorOptions exists and is an array
			if (!Array.isArray(item.colorOptions)) {
				return [];
			}

			// Return a sorted copy of the colorOptions array
			return item.colorOptions.slice().sort((a: any, b: any) => {
				const positionA = typeof a.position === "number" ? a.position : Infinity;
				const positionB = typeof b.position === "number" ? b.position : Infinity;

				return positionA - positionB;
			});
		},

		renderLineNumber(itemObj: Object) {
			//find the matching obj by index in the computedItemTable and return its line number (index + 1)
			let lineNumber = this.computedItemsTable.findIndex((obj: Object) => obj == itemObj);

			return lineNumber >= 0 ? lineNumber + 1 : "#";
		},

		renderPrice,

		renderItemTotal(item: LineItem) {
			const formattedTotal = getItemPrice(item)[1];
			return formattedTotal;
		},

		isNegativeTotal(item: LineItem) {
			const [numericValue] = getItemPrice(item);
			// Use the numeric value directly instead of parsing formatted string
			return numericValue < 0;
		},

		addMainItem() {
			if (this.locked) return; // locked quote (#1475)
			let newItem: LineItem = {
				itemId: null,
				systemCategoryId: 0,
				styleCode: "",
				description: "",
				price: 0,
				quantity: 1,
				default: "",
				colorOptions: null,
				sizeChart: null,
				sizeSelections: [],
				colorSelections: [],
				specialSizeSelections: [],
				lettering: null,
				total: "",
				dropShip: false,
				stockItems: false,
				note: "",
				children: [],
				specialCharge: 0.0,
				upCharge: 0,
				colorUpCharges: 0,
				sizeUpCharge: 0,
				packageCategoryId: 0,
				packageCategory: null,
				parentItemId: 0,
				includePrice: false,
				name: "",
				subItems: [],
				packageItems: [],
				phantomSubItems: [],
				subItemCategoryId: 0,
				inventoryItemId: 0,
				priceIncluded: false,
				freeTextSelections: null,
				lineItemId: undefined,
			};

			this.localTableData.push(newItem);

			this.$nextTick(() => {
				const lineNumber = this.computedItemsTable.length;
				this.scrollToItem(lineNumber);

				// Focus the style code input for the newly added item
				this.$nextTick(() => {
					const inputElement = (this as any).$el.querySelector(`[data-line="${lineNumber}"] input`);
					if (inputElement) {
						inputElement.focus();
					}
				});
			});
		},

		addSubitem(parentItem: LineItem, type?: string) {
			if (this.locked) return; // locked quote (#1475)
			//add a new child based item to the passed in parent item
			let newChildItem: LineSubItem = {
				itemData: {
					itemId: null,
					systemCategoryId: 0,
					styleCode: "",
					description: "",
					price: 0,
					quantity: 1,
					default: "",
					colorOptions: null,
					sizeChart: null,
					sizeSelections: [],
					colorSelections: [],
					specialSizeSelections: [],
					lettering: null,
					total: "",
					dropShip: false,
					stockItems: false,
					note: "",
					children: [],
					specialCharge: 0,
					upCharge: 0,
					colorUpCharges: 0,
					sizeUpCharge: 0,
					packageCategoryId: 0,
					packageCategory: null,
					parentItemId: 0,
					includePrice: false,
					name: "",
					subItems: [],
					packageItems: [],
					phantomSubItems: [],
					subItemCategoryId: parentItem.subItemCategoryId,
					inventoryItemId: 0,
					priceIncluded: false,
					freeTextSelections: null,
					lineItemId: undefined,
				},
				isDefault: false,
				priceIncluded: false,
				itemId: null,
				systemCategoryId: 0,
				styleCode: "",
				description: "",
				quantity: parentItem.quantity,
				default: "",
				includePrice: false,
				colorOptions: null,
				sizeChart: null,
				sizeSelections: [],
				colorSelections: [],
				specialSizeSelections: [],
				lettering: null,
				price: 0,
				dropShip: false,
				stockItems: false,
				note: "",
				children: [],
				specialCharge: 0,
				upCharge: 0,
				colorUpCharges: 0,
				sizeUpCharge: 0,
				total: "",
				packageCategoryId: 0,
				packageCategory: null,
				parentItemId: 0,
				name: "",
				subItems: [],
				packageItems: [],
				phantomSubItems: [],
				subItemCategoryId: 0,
				inventoryItemId: 0,
				freeTextSelections: null,
				lineItemId: undefined,
				braidPosition: null,
				uniqueId: this.generateUniqueId(),
				// type: type
			};

			//create an empty children array if gql data doesnt have any
			parentItem.children ? null : this.$set(parentItem, "children", []);

			if (parentItem.children == undefined) {
				parentItem.children = [];
			}

			//add the new child object
			parentItem.children.push(newChildItem);

			this.$nextTick(() => {
				const lineNumber = this.renderLineNumber(newChildItem);
				this.scrollToItem(lineNumber);

				// Focus the style code input for the newly added sub-item
				this.$nextTick(() => {
					const inputElement = (this as any).$el.querySelector(`[data-line="${lineNumber}"] input`);
					if (inputElement) {
						inputElement.focus();
					}
				});
			});
		},

		removeSubitem(childItem: LineItem, parentItem?: LineItem) {
			if (this.locked) return; // locked quote (#1475)
			//if no parent item then remove the childItem from main data otherwise if parent has children data then remove the child item
			if (!parentItem) {
				this.localTableData = this.localTableData.filter((obj: LineItem) => obj !== childItem);
			} else {
				parentItem.children = parentItem.children.filter((obj: LineItem) => obj !== childItem);
			}
		},

		updateItemQuantities(mainItem: LineItem, parentItem?: LineItem) {
			if (this.locked) return; // locked quote (#1475)
			// Update item quantities of any children items to match the passed-in item's quantity
			if (mainItem.children && mainItem.children.length > 0) {
				mainItem.children.forEach((child) => {
					// Track old quantity BEFORE any updates to determine if grandchildren need updating
					const oldChildQuantity = child.quantity;

					// Calculate the base quantity using the quantifier
					const quantifier = this.quantityQuantifier(child);
					// Always use mainItem.quantity for calculating children's base quantities
					// Round to absorb floating-point drift from the normalized quantifier (e.g. 1/49 * 48)
					const baseQuantity = Math.round(quantifier * mainItem.quantity);

					// Check if this child has a manual override (upward or downward)
					if (child.isManuallyOverridden) {
						const currentQuantity = child.quantity;

						if (currentQuantity > baseQuantity) {
							// Upward override - preserve current quantity and recalculate override
							this.$set(child, 'baseQuantity', baseQuantity);
							this.$set(child, 'quantity', currentQuantity);
							this.$set(child, 'quantityOverride', currentQuantity - baseQuantity);

							// Update extraItemsPrice for upward override
							if (child.price && child.quantityOverride > 0) {
								this.$set(child, 'extraItemsPrice', child.price * child.quantityOverride);
							}
						} else if (currentQuantity === baseQuantity) {
							// Natural scaling matched override - clear it
							this.$set(child, 'quantity', baseQuantity);
							this.$set(child, 'baseQuantity', baseQuantity);
							this.$set(child, 'isManuallyOverridden', false);
							this.$set(child, 'quantityOverride', 0);
							this.$set(child, 'extraItemsPrice', 0);
						} else {
							// Downward override - preserve current quantity (e.g., 4 of 5 jackets decorated)
							this.$set(child, 'baseQuantity', baseQuantity);
							this.$set(child, 'quantity', Math.max(1, currentQuantity));
							this.$set(child, 'quantityOverride', Math.max(1, currentQuantity) - baseQuantity);
							this.$set(child, 'extraItemsPrice', 0);
						}
					} else {
						// Standard auto-increment behavior - no override
						this.$set(child, 'quantity', baseQuantity);
						this.$set(child, 'baseQuantity', baseQuantity);
						// Reset override state if there's no actual override
						if (child.isManuallyOverridden && (!child.quantityOverride || child.quantityOverride === 0)) {
							this.$set(child, 'isManuallyOverridden', false);
							this.$set(child, 'quantityOverride', 0);
						}
					}

					// Only update grandchildren if this child's quantity actually changed
					// This prevents unnecessary recalculations when parent override is preserved
					if (child.children && child.children.length > 0 && child.quantity !== oldChildQuantity) {
						child.children.forEach((subChild) => {
							const subQuantifier = this.quantityQuantifier(subChild);
							const subBaseQuantity = Math.round(subQuantifier * child.quantity);

							// Check if grandchild has manual override (upward or downward)
							if (subChild.isManuallyOverridden) {
								const subCurrentQuantity = subChild.quantity;

								if (subCurrentQuantity > subBaseQuantity) {
									// Upward override - preserve current quantity and recalculate override
									this.$set(subChild, 'baseQuantity', subBaseQuantity);
									this.$set(subChild, 'quantity', subCurrentQuantity);
									this.$set(subChild, 'quantityOverride', subCurrentQuantity - subBaseQuantity);

									// Update extraItemsPrice
									if (subChild.price && subChild.quantityOverride > 0) {
										this.$set(subChild, 'extraItemsPrice', subChild.price * subChild.quantityOverride);
									}
								} else if (subCurrentQuantity === subBaseQuantity) {
									// Natural scaling matched override - clear it
									this.$set(subChild, 'quantity', subBaseQuantity);
									this.$set(subChild, 'baseQuantity', subBaseQuantity);
									this.$set(subChild, 'isManuallyOverridden', false);
									this.$set(subChild, 'quantityOverride', 0);
									this.$set(subChild, 'extraItemsPrice', 0);
								} else {
									// Downward override - preserve current quantity
									this.$set(subChild, 'baseQuantity', subBaseQuantity);
									this.$set(subChild, 'quantity', Math.max(1, subCurrentQuantity));
									this.$set(subChild, 'quantityOverride', Math.max(1, subCurrentQuantity) - subBaseQuantity);
									this.$set(subChild, 'extraItemsPrice', 0);
								}
							} else {
								this.$set(subChild, 'quantity', subBaseQuantity);
								this.$set(subChild, 'baseQuantity', subBaseQuantity);
								// Reset override state if there's no actual override
								if (subChild.isManuallyOverridden && (!subChild.quantityOverride || subChild.quantityOverride === 0)) {
									this.$set(subChild, 'isManuallyOverridden', false);
									this.$set(subChild, 'quantityOverride', 0);
								}
							}
						});
					}
				});
			}

			// If a parentItem is passed in and the item is NOT manually overridden,
			// ensure the mainItem's quantity is not less than its parent's quantity.
			// Manually overridden items are allowed to go below parent quantity
			// (e.g., only 4 of 5 jackets getting a specific decoration).
			if (parentItem && mainItem.quantity < parentItem.quantity && !mainItem.isManuallyOverridden) {
				// Use $nextTick to ensure this runs after Vue's reactivity updates have been processed
				this.$nextTick(() => {
					this.$set(mainItem, "quantity", parentItem.quantity);
				});
			}

			// If the mainItem's quantity is less than 1, handle the sizing accordingly
			if (mainItem.quantity < 1) {
				this.showItemSizingByLine = null;
			}
		},

		hideItemSearch() {
			// Clear any pending timeout
			if (this.hideSearchTimeout !== null) {
				clearTimeout(this.hideSearchTimeout);
			}

			if (this.preventCloseOnBlur) {
				// Don't close the dropdown - just reset the flag after a delay
				// This allows keyboard navigation and clicking on dropdown items
				this.hideSearchTimeout = setTimeout(() => {
					this.preventCloseOnBlur = false;
					this.hideSearchTimeout = null;
				}, 200) as unknown as number;
			} else {
				// Close the dropdown
				this.hideSearchTimeout = setTimeout(() => {
					this.showItemSearchByLine = null;
					this.hideSearchTimeout = null;
				}, 150) as unknown as number;
			}
		},

		clearDraftText(item: LineItem | LineSubItem) {
			// Clear draft placeholder text when user focuses/clicks the input
			if (item.itemId === -1 && (item.styleCode === '[Draft Item]' || item.styleCode === 'DRAFT')) {
				item.styleCode = '';
			}
		},

		//https://en.wikipedia.org/wiki/Levenshtein_distance
		levenshteinDistance(a, b) {
			const m = a.length;
			const n = b.length;

			// Create a 2D array to store the distances
			const dp = Array(m + 1)
				.fill(null)
				.map(() => Array(n + 1).fill(0));

			// Initialize the base cases
			for (let i = 0; i <= m; i++) {
				dp[i][0] = i;
			}
			for (let j = 0; j <= n; j++) {
				dp[0][j] = j;
			}

			// Calculate the Levenshtein distance
			for (let i = 1; i <= m; i++) {
				for (let j = 1; j <= n; j++) {
					if (a[i - 1] === b[j - 1]) {
						dp[i][j] = dp[i - 1][j - 1];
					} else {
						dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
					}
				}
			}

			// Return the Levenshtein distance
			return dp[m][n];
		},

		/**
		 * Filter items by search term (case-insensitive substring match)
		 * @param items - Array of items to filter
		 * @param searchTerm - Search term to filter by
		 * @returns Filtered array
		 */
		filterBySearchTerm(items: any[], searchTerm: string): any[] {
			if (!searchTerm || searchTerm.trim() === '') {
				return items; // No filtering if empty search
			}

			const term = searchTerm.toLowerCase().trim();

			return items.filter(item => {
				const styleCode = (item.styleCode || '').toLowerCase();
				return styleCode.includes(term);
			});
		},

		/**
		 * Classify the type of match between search term and style code
		 * Returns priority (lower = better) and distance for tie-breaking
		 * @param styleCode - The style code to check
		 * @param searchTerm - The search term to match against
		 * @returns Object with priority and distance
		 */
		classifyMatch(styleCode: string, searchTerm: string): { priority: number; distance: number } {
			const term = searchTerm.toLowerCase().trim();
			const code = styleCode.toLowerCase();

			// Priority 1: Exact match (case-insensitive)
			if (code === term) {
				return { priority: 1, distance: 0 };
			}

			// Priority 2: Starts with search term
			if (code.startsWith(term)) {
				return { priority: 2, distance: code.length - term.length };
			}

			// Priority 3: Contains search term
			if (code.includes(term)) {
				const index = code.indexOf(term);
				return { priority: 3, distance: index };
			}

			// Priority 4: Fuzzy match (Levenshtein distance)
			const distance = this.levenshteinDistance(code, term);
			return { priority: 4, distance };
		},

		async searchItems(item: LineItem, level: number, parentItem?: LineItem) {
			if (this.locked) return; // locked quote (#1475)
			// Cancel any pending hideItemSearch timeout to prevent race conditions
			if (this.hideSearchTimeout !== null) {
				clearTimeout(this.hideSearchTimeout);
				this.hideSearchTimeout = null;
			}

			this.itemSearchResults = [];
			let isPackage = item.packageCategoryId && item.packageCategoryId > 0;
			let packageItems = [];
			let subItemCategoryItems = [];

			// Phase 1: Fetch and filter package items by search term
			if (isPackage) {
				const allPackageItems = await this.fetchAndFilterItems(
					PACKAGE_CATEGORY_QUERY,
					item.packageCategoryId,
					(data) =>
						data.packageCategoryById.items
							.filter((i) => !i.inactiveDate)
							.map((i) => ({ ...i, isPackageItem: true }))
				);
				// Apply client-side filtering by search term
				packageItems = this.filterBySearchTerm(allPackageItems, item.styleCode);
			}

			// Phase 1: Fetch and filter sub-item category items by search term
			if (item.subItemCategoryId && item.subItemCategoryId > 0) {
				const allSubItemCategoryItems = await this.fetchAndFilterItems(
					SUB_ITEM_CATEGORY_QUERY,
					item.subItemCategoryId,
					(data) =>
						data.subItemCategoryById.subItems
							.filter((i) => !i.inactiveDate)
							.map((i) => ({ ...i, isPackageItem: isPackage }))
				);
				// Apply client-side filtering by search term
				subItemCategoryItems = this.filterBySearchTerm(allSubItemCategoryItems, item.styleCode);
			}

			// Category-bound sub-item rows are exclusive (#1462): only members of the category may
			// be offered, so the general inventory search is skipped entirely. A term that matches
			// nothing in the category yields an empty dropdown — never the unfiltered global list.
			const isCategoryBound =
				!isPackage && item.subItemCategoryId && item.subItemCategoryId > 0;

			let allResults: any[] = [];

			if (isCategoryBound) {
				allResults = [...subItemCategoryItems];
			} else {
				// Fetch GraphQL results (already filtered by backend)
				let results = isPackage
					? await this.$apollo.query({
							query: SEARCH_INVENTORY_LISTINGS_BY_SYSTEMCATEGORYID_QUERY, // search upgradeable package items
							variables: {
								searchTerm: item.styleCode,
								systemCategoryId: item.systemCategoryId,
							},
							fetchPolicy: "no-cache",
					  })
					: await this.$apollo.query({
							query: SEARCH_INVENTORY_LISTINGS_QUERY, // general search
							variables: {
								searchTerm: item.styleCode,
								itemTypes: this.determineItemTypes(level, parentItem),
							},
							fetchPolicy: "no-cache",
					  });

				let gqlResults = results.data?.inventoryListings?.items || [];

				// Add category-specific results first (prioritize specific categories)
				if (subItemCategoryItems.length > 0) {
					allResults = [...subItemCategoryItems];
				} else if (packageItems.length > 0) {
					allResults = [...packageItems];
				}

				// Merge with general search results
				if (gqlResults.length > 0) {
					// Remove duplicates (prefer category-specific results)
					const existingIds = new Set(allResults.map(r => r.inventoryItemId));
					const uniqueGqlResults = gqlResults.filter(r => !existingIds.has(r.inventoryItemId));
					allResults = [...allResults, ...uniqueGqlResults];
				}
			}

			// Phase 2: Sort with multi-tier algorithm (exact → prefix → contains → fuzzy → alphabetical)
			const searchTerm = item.styleCode || '';
			allResults.sort((a, b) => {
				const aMatch = this.classifyMatch(a.styleCode, searchTerm);
				const bMatch = this.classifyMatch(b.styleCode, searchTerm);

				// First sort by priority (lower = better)
				if (aMatch.priority !== bMatch.priority) {
					return aMatch.priority - bMatch.priority;
				}

				// Then by distance (lower = better)
				if (aMatch.distance !== bMatch.distance) {
					return aMatch.distance - bMatch.distance;
				}

				// Finally alphabetically
				return a.styleCode.localeCompare(b.styleCode);
			});

			// Phase 5: Limit results for performance
			const MAX_RESULTS = 50;
			if (allResults.length > MAX_RESULTS) {
				allResults = allResults.slice(0, MAX_RESULTS);
			}

			this.itemSearchResults = allResults;
			this.showItemSearchByLine = this.renderLineNumber(item);
			this.focusedItemIndex = -1; // Reset keyboard navigation index for new dropdown
		},

		async fetchAndFilterItems(
			query: any,
			filterId: number,
			filterFunction: (data: any) => InventoryItem[]
		) {
			try {
				const { data } = await this.$apollo.query({
					query: query,
					variables: { id: filterId },
					fetchPolicy: "no-cache",
				});
				return filterFunction(data);
			} catch (error) {
				console.error(error);
				return [];
			}
		},

		determineItemTypes(level: number, parentItem?: LineItem): string[] {
			if (level === 1) return ["Item", "Package"];
			if (level === 2 && parentItem && parentItem.type === "Package") return ["Item"];
			return ["SubItem"];
		},

		/**
		 * This takes the existing item reference and the new item and update the new
		 * item using fields from the existing item.
		 */
		NewItemFromCategoryItem(exsitingItem: any, newItem: any, attributes: string[]) {
			attributes.forEach((attribute: string) => {
				const value = exsitingItem[attribute];

				if (value && value !== null) {
					newItem[attribute] = value;
				}
			});

			return newItem;
		},

		// After a style swap/re-select, the freshly-fetched item carries the new unit price but
		// none of the package quantity-override context. The override fields (baseQuantity,
		// quantityOverride, isManuallyOverridden) are carried over via NewItemFromCategoryItem;
		// this recomputes the display-only extras charge so it reflects the NEW style's price.
		refreshExtraItemsPrice(item: any) {
			const hasUpwardOverride =
				item.isManuallyOverridden && (item.quantityOverride || 0) > 0;
			this.$set(
				item,
				"extraItemsPrice",
				hasUpwardOverride ? (item.price || 0) * item.quantityOverride : 0
			);
		},

		async setItem(
			currentItem: LineSubItem | LineItem,
			resultItem: LineSubItem,
			parentIndex: number,
			subIndex?: number | null,
			subIndex2?: number | null,
			type?: string,
			lineItemId?: number | null | undefined
		) {
			this.showItemSearchByLine = null;

			if (!resultItem || !resultItem.inventoryItemId) return;

			// Commit the pending undo snapshot (captured on search field focus)
			// Pass the new item's styleCode for the toast message
			this.commitUndoSnapshot(parentIndex, resultItem.styleCode);

			let selectedItem: LineSubItem = {} as LineSubItem;
			let isPackageAndFirstLoad = false;

			// ===== Capture current item state before any changes =====
			// Check both inventoryItemId (preferred) and itemId for compatibility
			const currentItemId = currentItem.inventoryItemId || currentItem.itemId;
			const currentChildren = currentItem.children ? [...currentItem.children] : [];
			const hasExistingChildren = currentChildren.length > 0;


			if (resultItem.systemCategoryId == 1) {
				selectedItem = await this.getInventoryPackage(resultItem.inventoryItemId);
				isPackageAndFirstLoad = true;
			} else if (resultItem.type === "SubItem" || this.$systemCategories.isSubItem(resultItem.systemCategoryId)) {
				selectedItem = await this.getInventorySubItem(resultItem.inventoryItemId);
				if ("braidPosition" in resultItem && resultItem.braidPosition)
					selectedItem.braidPosition = resultItem.braidPosition;
			} else if (resultItem.inventoryItemId) {
				selectedItem = await this.getInventoryItem(resultItem.inventoryItemId);
			}

			// Check if item was successfully fetched
			if (!selectedItem || Object.keys(selectedItem).length === 0) {
				console.error(`Failed to fetch inventory item with ID: ${resultItem.inventoryItemId}, type: ${resultItem.type}`);
				return;
			// ===== END same-item check =====
			}

			if (
				currentItem.packageCategoryId &&
				currentItem.packageCategoryId > 0 &&
				!(resultItem as any).isPackageItem
			) {
				try {
					const confirmedItem = await this.confirmAction(selectedItem);
					// Continue processing with the confirmed item
				} catch (error) {
					console.error("Confirmation failed:", error);
					return; // Stop further processing since confirmation was not given
				}
			}

			if (resultItem.type) {
				selectedItem.type = resultItem.type;
			}

			if (!selectedItem.type) {
				if (this.$systemCategories.isSubItem(resultItem.systemCategoryId)) {
					selectedItem.type = "SubItem";
				} else if (this.$systemCategories.isItem(resultItem.systemCategoryId)) {
					selectedItem.type = "Item";
				}
			}

			// ===== Determine if we should preserve existing sub-items =====
			const newItemId = selectedItem?.inventoryItemId || selectedItem?.itemId || resultItem.inventoryItemId;

			// Draft items (itemId === -1) should not trigger preservation
			// They are placeholders that should be fully replaced
			const isDraftItem = currentItemId === -1 || newItemId === -1;

			// Check if this is the same item (user pressed Tab without changing)
			const isSameItem = !isDraftItem && currentItemId === newItemId;

			// Only preserve sub-items when re-selecting the exact same item (e.g. Tab without changing).
			// When switching to a different parent item, old sub-items must be removed so that only
			// the new parent's sub-items are shown (see issue #1212).
			const shouldPreserveSubItems = hasExistingChildren && isSameItem;


			//check what level we are updating there are only 3 levels
			if (parentIndex !== undefined && subIndex == undefined && subIndex2 == undefined) {
				//update top level items with the selected search result item
				if (!selectedItem.quantity) {
					selectedItem.quantity = 1;
				}

				const generatedSubItems = await this.processSubItems(selectedItem, selectedItem.quantity);

				// ===== Apply intelligent merging for level 1 =====
			let finalChildren;

				if (shouldPreserveSubItems) {
					// Use intelligent merging to preserve existing sub-items
				finalChildren = this.mergeSubItemsIntelligently(
					currentChildren,
					generatedSubItems,
						true  // preserveAll = true
					);
			} else {
					const currentPackageChildren = currentItem.children?.filter((c: any) => c.packageId) || [];
					const rawGeneratedSubItems = Array.from(generatedSubItems);
					const rawCurrentChildren = Array.from(currentPackageChildren);
					finalChildren = isPackageAndFirstLoad
						? generatedSubItems
						: [...rawGeneratedSubItems, ...rawCurrentChildren];
				}

				this.$set(selectedItem, "children", finalChildren);
			// ===== PRESERVE PARENT ITEM'S OWN SELECTIONS =====
			// When re-selecting the same item, preserve the parent's own color/size selections.
			// When switching items, explicitly clear to prevent stale SizeIds from persisting (#1392).
			if (isSameItem) {
				if (currentItem.colorSelections) selectedItem.colorSelections = currentItem.colorSelections;
				if (currentItem.sizeSelections) selectedItem.sizeSelections = currentItem.sizeSelections;
				if (currentItem.specialSizeSelections) selectedItem.specialSizeSelections = currentItem.specialSizeSelections;
				if (currentItem.freeTextSelections) selectedItem.freeTextSelections = currentItem.freeTextSelections;
			} else {
				selectedItem.sizeSelections = [];
				selectedItem.specialSizeSelections = [];
			}
			// ===== END PARENT ITEM SELECTION PRESERVATION =====


				this.localTableData.splice(parentIndex, 1, selectedItem);
			} else if (parentIndex !== undefined && subIndex !== undefined && subIndex2 == undefined) {
				//update level 2 items of the passed in parentItem at the provided subIndex
				// Grab reference to the existing item
				let existingItem: LineItem = this.localTableData[parentIndex].children[subIndex];

				// JS passes objects/arrays by reference. Therefore we don't technically need to assign back to the new item.
				this.NewItemFromCategoryItem(existingItem, selectedItem, [
					"quantity",
					"priceIncluded",
					"includePrice",
					"braidPosition",
					// The category binding belongs to the row (slot), not the picked style — without
					// this the dropdown loses its category filter after the first selection (#1462).
					"subItemCategoryId",
					// Preserve the package quantity-override context so swapping/re-selecting a style
					// does not lose the "+N over package quantity" intent (see issue #1394). The
					// override is tied to the package slot, not the specific style, so it survives a swap.
					"baseQuantity",
					"quantityOverride",
					"isManuallyOverridden",
				]);

				// Recompute the display-only extras charge against the new style's price.
				this.refreshExtraItemsPrice(selectedItem);

				// Default to quantity of 1 if not set
				if (!selectedItem.quantity) {
					selectedItem.quantity = 1;
				}

				//assign a packageCategoryId for item searches if the level 2 item was a generated child item
				if (existingItem.packageCategoryId) {
					selectedItem.packageCategoryId = existingItem.packageCategoryId;
				}

				const generatedSubItems = await this.processSubItems(selectedItem, selectedItem.quantity);

				// ===== Apply intelligent merging for level 2 =====
				let finalChildren;

				// For level 2, only preserve sub-items when re-selecting the exact same item.
				// When switching to a different parent item, old sub-items must be removed so that only
				// the new parent's sub-items are shown (see issue #1212).
				const level2CurrentChildren = existingItem.children || [];
				const level2SelectedId = selectedItem.inventoryItemId || selectedItem.itemId;
				const level2ShouldPreserve = level2CurrentChildren.length > 0 &&
					existingItem.itemId === level2SelectedId;
				// Item-only match for preserving this line's own size/color selections (#1392):
				// decoupled from sub-item preservation so a sized item with no sub-items keeps its sizes.
				const level2SameItem = existingItem.itemId === level2SelectedId;

				if (level2ShouldPreserve) {
				finalChildren = this.mergeSubItemsIntelligently(
					level2CurrentChildren,
					generatedSubItems,
						true
					);
				} else {
					const currentPackageChildren = currentItem.children?.filter((c: any) => c.packageId) || [];
					const rawGeneratedSubItems = Array.from(generatedSubItems);
					const rawCurrentChildren = Array.from(currentPackageChildren);
					finalChildren = isPackageAndFirstLoad
						? generatedSubItems
						: [...rawGeneratedSubItems, ...rawCurrentChildren];
				}

				this.$set(selectedItem, "children", finalChildren);
			// ===== END level 2 merging =====

		// Preserve level 2 item's own selections when re-selecting same item.
		// When switching items, explicitly clear to prevent stale SizeIds (#1392).
		if (level2SameItem) {
			if (existingItem.colorSelections) selectedItem.colorSelections = existingItem.colorSelections;
			if (existingItem.sizeSelections) selectedItem.sizeSelections = existingItem.sizeSelections;
			if (existingItem.specialSizeSelections) selectedItem.specialSizeSelections = existingItem.specialSizeSelections;
			if (existingItem.freeTextSelections) selectedItem.freeTextSelections = existingItem.freeTextSelections;
		} else {
			selectedItem.sizeSelections = [];
			selectedItem.specialSizeSelections = [];
		}


				// Update the existing item with the new item
				this.localTableData[parentIndex].children.splice(subIndex, 1, selectedItem);
			} else if (subIndex !== undefined && subIndex2 !== undefined) {
				//update level 3 items of the passed in parentItems subIndex at the provided subIndex2
				// Grab reference to the existing item
				let existingItem: LineItem =
					this.localTableData[parentIndex].children[subIndex].children[subIndex2];

				// JS passes objects/arrays by reference. Therefore we don't technically need to assign back to the new item.
				this.NewItemFromCategoryItem(existingItem, selectedItem, [
					"quantity",
					"priceIncluded",
					"includePrice",
					"braidPosition",
					"subItemCategoryId",
					// Preserve the package quantity-override context so swapping/re-selecting a style
					// does not lose the "+N over package quantity" intent (see issue #1394).
					"baseQuantity",
					"quantityOverride",
					"isManuallyOverridden",
				]);

				// Recompute the display-only extras charge against the new style's price.
				this.refreshExtraItemsPrice(selectedItem);

				// Default to quantity of 1 if not set
				if (!selectedItem.quantity) {
					selectedItem.quantity = 1;
				}

				//assign a packageCategoryId for item searches if the level 3 item was a generated child item
				if (existingItem.packageCategoryId) {
					selectedItem.packageCategoryId = existingItem.packageCategoryId;
				}
				// selectedItem.type = type;

				// Only preserve selections when re-selecting the exact same item (see issue #1212)
				const level3SelectedId = selectedItem.inventoryItemId || selectedItem.itemId;
				const level3ShouldPreserve = existingItem.itemId === level3SelectedId;

				// ===== Apply intelligent merging for level 3 (if it has children) =====
				// Note: Level 3 items typically don't have children, but handle it just in case
				if (selectedItem.children || existingItem.children) {
					const generatedSubItems = selectedItem.children || [];
					const level3CurrentChildren = existingItem.children || [];

					if (level3ShouldPreserve) {
					selectedItem.children = this.mergeSubItemsIntelligently(
						level3CurrentChildren,
						generatedSubItems,
							true
						);
					}
				}
			// ===== END level 3 merging =====

				// Preserve level 3 item's own selections when re-selecting same item.
				// When switching items, explicitly clear to prevent stale SizeIds (#1392).
				if (level3ShouldPreserve) {
					if (existingItem.colorSelections) selectedItem.colorSelections = existingItem.colorSelections;
					if (existingItem.sizeSelections) selectedItem.sizeSelections = existingItem.sizeSelections;
					if (existingItem.specialSizeSelections) selectedItem.specialSizeSelections = existingItem.specialSizeSelections;
					if (existingItem.freeTextSelections) selectedItem.freeTextSelections = existingItem.freeTextSelections;
				} else {
					selectedItem.sizeSelections = [];
					selectedItem.specialSizeSelections = [];
				}


				// Update the existing item with the new item
				this.localTableData[parentIndex].children[subIndex].children.splice(
					subIndex2,
					1,
					selectedItem
				);
			}

			//keep existing lineItemId if present
			selectedItem.lineItemId = lineItemId;

			//reset the search display
			this.showItemSearchByLine = null;

			// Focus quantity input after item is set
			this.$nextTick(() => {
				const currentRow = document.querySelector(
					`[data-row-line="${this.renderLineNumber(selectedItem)}"]`
				);
				if (currentRow) {
					// The inventory fetch above can take a while; if the user has already
					// moved on to typing in another field (e.g. the next line's SKU search),
					// stealing focus back here would dump their keystrokes into this row's
					// quantity box. Only hand off focus if they haven't gone elsewhere.
					const active = document.activeElement;
					const userMovedToAnotherField =
						(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) &&
						!currentRow.contains(active);
					if (userMovedToAnotherField) return;

					const quantityInput = currentRow.querySelector(".number-input input");
					if (quantityInput) {
						(quantityInput as HTMLElement).focus();
					}
				}
			});
		},

		confirmAction(selectedItem) {
			return new Promise((resolve, reject) => {
				// Emit onSave and expect the parent to call the resolve method with necessary data
				this.$emit(
					"onSave",
					(response) => {
						selectedItem.upChargeDescription = response.upChargeDescription;
						selectedItem.upCharge = Number(response.upCharge);
						resolve(selectedItem);
					},
					reject
				);
			});
		},

		editUpcharge(selectedItem) {
			if (this.locked) return; // locked quote (#1475)
			return new Promise((resolve, reject) => {
				this.$emit(
					"onSave",
					(response) => {
						selectedItem.upChargeDescription = response.upChargeDescription;
						selectedItem.upCharge = Number(response.upCharge);
						resolve(selectedItem);
					},
					reject,
					selectedItem
				);
			});
		},

		async processSubItems(item: LineSubItem, parentQuantity: any) {
			let children: any = [];

			const processSubItem = async (sub: any) => {
				let fullItem;
				let inventoryItemId =
					sub?.itemData?.inventoryItemId ||
					sub?.inventoryItemId ||
					sub?.packageCategory?.inventoryItemId;

				if (inventoryItemId) {
					fullItem = await this.getAnyInventoryItem(inventoryItemId);
				}

				if (inventoryItemId === 42) {
					console.log(inventoryItemId, "subItem FORCED", sub, fullItem);
				}

				return sub.subItemCategoryId
					? {
							...sub,
							itemId: null,
							parentItemId: sub.parentItemId,
							subItemCategoryId: sub.subItemCategoryId,
							systemCategoryId: sub.subItemCategory.systemCategoryId,
							placeholder: sub.subItemCategory.name,
							styleCode: "", // sub.itemData.name - OES-265
							description: "", // sub.itemData.description - OES-265
							quantity: sub.quantity * item.quantity,
							default: sub.default, //not sure what to do with this?
							includePrice: sub.priceIncluded, //not sure what to do with this?
							colorOptions: null,
							sizeChart: null,
							sizeSelections: [],
							colorSelections: [],
							specialSizeSelections: [],
							lettering: null,
							price: 0,
							dropShip: false,
							note: "",
							type: "SubItem",
							children: await this.processSubItems(sub, sub.quantity * parentQuantity),
					  }
					: {
							...sub,
							itemId: inventoryItemId,
							parentItemId: sub.parentItemId,
							systemCategoryId:
								sub.itemData?.systemCategoryId ||
								fullItem?.systemCategoryId ||
								sub?.systemCategoryId,
							styleCode: sub.itemData?.styleCode || fullItem?.styleCode,
							description: sub.itemData?.description || fullItem?.description,
							quantity: sub.quantity * parentQuantity,
							isDefault: sub.isDefault,
							includePrice: sub.priceIncluded,
							colorOptions: sub.itemData?.colorOptions || fullItem?.colorOptions,
							sizeChart: sub.itemData?.sizeChart || fullItem?.sizeChart,
							price: (sub.itemData?.price || fullItem?.price) ?? 0,
							dropShip: sub.itemData?.dropShip,
							packageCategoryId: sub.packageCategoryId,
							subItemCategoryId: sub.subItemCategoryId,
							inventoryItemId: inventoryItemId,
							type: "SubItem",
							braidPosition: sub.braidPosition,
							children: await this.processSubItems(sub, sub.quantity * parentQuantity),
					  };
			};

			// Process regular subItems
			if (item.subItems && item.subItems.length > 0) {
				const subItemsPromises = item.subItems.map((sub) => processSubItem(sub));
				children = children.concat(await Promise.all(subItemsPromises));
			}

			// Process phantomSubItems
			if (item.phantomSubItems && item.phantomSubItems.length > 0) {
				const phantomSubItemsPromises = item.phantomSubItems.map(async (sub) => {
					return {
						...sub,
						itemId: null,
						parentItemId: sub.parentItemId,
						subItemCategoryId: sub.subItemCategoryId,
						systemCategoryId: sub.itemData.systemCategoryId,
						placeholder: sub.itemData.name,
						styleCode: "", // sub.itemData.name - OES-265
						description: "", // sub.itemData.description - OES-265
						quantity: sub.quantity * item.quantity,
						default: sub.default, //not sure what to do with this?
						includePrice: sub.priceIncluded, //not sure what to do with this?
						colorOptions: null,
						sizeChart: null,
						sizeSelections: [],
						colorSelections: [],
						specialSizeSelections: [],
						lettering: null,
						price: 0,
						dropShip: false,
						note: "",
						children: [],
						type: "SubItem",
					};
				});
				children = children.concat(await Promise.all(phantomSubItemsPromises));
			}

			// Process packageItems
			if (item.packageItems && item.packageItems.length > 0) {
				const packageItemsPromises = item.packageItems.map(async (sub) => {
					let fullItem;
					if (this.$systemCategories.isSubItem(sub?.packageCategory?.inventoryItemId)) {
						fullItem = await this.getInventorySubItem(sub?.packageCategory?.inventoryItemId);
					} else if (sub?.packageCategory?.inventoryItem?.inventoryItemId) {
						fullItem = await this.getInventoryItem(sub?.packageCategory?.inventoryItemId);
					}

					return {
						...sub,
						itemId: sub?.packageCategory?.inventoryItem?.inventoryItemId,
						packageCategoryId: sub.packageCategoryId,
						systemCategoryId: sub.packageCategory.systemCategoryId,
						placeholder: sub.packageCategory.name,
						styleCode: sub?.packageCategory?.inventoryItem?.styleCode ?? "",
						description: sub?.packageCategory?.inventoryItem?.description ?? "",
						quantity: sub.quantity * item.quantity,
						default: sub.isDefault,
						includePrice: sub.priceIncluded,
						colorOptions: fullItem?.colorOptions,
						sizeChart: fullItem?.sizeChart,
						sizeSelections: [],
						colorSelections: [],
						specialSizeSelections: [],
						lettering: null,
						price: fullItem?.price ?? 0,
						dropShip: false,
						note: "",
						children: await this.processSubItems(sub, sub.quantity * item.quantity),
						type: sub?.packageCategory?.inventoryItem?.type,
					};
				});
				children = children.concat(await Promise.all(packageItemsPromises));
			}

			return children;
		},

	 /**
	  * ===== Intelligent Sub-Item Merging =====
		 * Intelligently merges existing sub-items with newly generated sub-items
		 * Preserves user-entered data while adding new sub-items if needed
		 *
		 * @param existingChildren - Current sub-items with user data
		 * @param generatedChildren - Freshly generated sub-items from new parent item
		 * @param preserveAll - If true, keep all existing items; if false, only merge matching ones
		 * @param depth - Current recursion depth (internal use, starts at 0)
		 * @param maxDepth - Maximum recursion depth to prevent stack overflow (default 10)
		 * @returns Merged array of sub-items
		 */
		mergeSubItemsIntelligently(
			existingChildren: any[],
			generatedChildren: any[],
			preserveAll: boolean = true,
			depth: number = 0,
			maxDepth: number = 10
		): any[] {
			// Prevent infinite recursion from circular references or malformed data
			if (depth >= maxDepth) {
				console.error(`[mergeSubItemsIntelligently] Max recursion depth (${maxDepth}) reached. Returning generated children to prevent stack overflow.`);
				return generatedChildren;
			}

			const merged: any[] = [];
			const processedExistingIndices: Set<number> = new Set();

			// Phase 1: Try to match generated items with existing items
			for (const generated of generatedChildren) {
				let matchedExisting = null;
				let matchedIndex = -1;

				// Try to find matching existing sub-item
				for (let i = 0; i < existingChildren.length; i++) {
					if (processedExistingIndices.has(i)) continue; // Already matched

					const existing = existingChildren[i];

					// Match criteria (in priority order):
					// 1. Same inventoryItemId (exact item match)
					// 2. Same subItemCategoryId (same category placeholder)
					// 3. Same packageCategoryId (same package item)

					const matchByInventoryId =
						existing.inventoryItemId &&
						generated.inventoryItemId &&
						existing.inventoryItemId === generated.inventoryItemId;

					const matchBySubItemCategory =
						existing.subItemCategoryId &&
						generated.subItemCategoryId &&
						existing.subItemCategoryId === generated.subItemCategoryId;

					const matchByPackageCategory =
						existing.packageCategoryId &&
						generated.packageCategoryId &&
						existing.packageCategoryId === generated.packageCategoryId;

					if (matchByInventoryId || matchBySubItemCategory || matchByPackageCategory) {
						matchedExisting = existing;
						matchedIndex = i;
						break;
					}
				}

				if (matchedExisting) {
					// Found a match - preserve existing item's data but update structure from generated
					merged.push({
						...generated,  // Use generated item's structure
						// ===== CRITICAL: Preserve the actual selected item metadata =====
						// Without these, re-selecting the parent wipes out which items were selected
						inventoryItemId: matchedExisting.inventoryItemId || generated.inventoryItemId,
						itemId: matchedExisting.itemId || generated.itemId,
						styleCode: matchedExisting.styleCode || generated.styleCode,
						description: matchedExisting.description || generated.description,
						systemCategoryId: matchedExisting.systemCategoryId || generated.systemCategoryId,
						type: matchedExisting.type || generated.type,
						vendorCost: matchedExisting.vendorCost !== undefined ? matchedExisting.vendorCost : generated.vendorCost,
						// CRITICAL: Preserve size/color metadata that determines if item needs selections
						sizeChart: matchedExisting.sizeChart || generated.sizeChart,
						colorOptions: matchedExisting.colorOptions || generated.colorOptions,
						// ===== END ITEM METADATA PRESERVATION =====
						// Preserve user-entered data from existing item
						lineItemId: matchedExisting.lineItemId,
						quantity: matchedExisting.quantity,
						price: matchedExisting.price,
						upCharge: matchedExisting.upCharge,
						sizeUpCharge: matchedExisting.sizeUpCharge,
						note: matchedExisting.note,
						colorSelections: matchedExisting.colorSelections,
						sizeSelections: matchedExisting.sizeSelections,
						specialSizeSelections: matchedExisting.specialSizeSelections,
						freeTextSelections: matchedExisting.freeTextSelections,
						lettering: matchedExisting.lettering,
						braidPosition: matchedExisting.braidPosition,
						dropShip: matchedExisting.dropShip,
						includePrice: matchedExisting.includePrice,
						priceIncluded: matchedExisting.priceIncluded,
						// Recursively merge children (with depth tracking to prevent infinite loops)
						children: matchedExisting.children && matchedExisting.children.length > 0
							? this.mergeSubItemsIntelligently(
								matchedExisting.children,
								generated.children || [],
								preserveAll,
								depth + 1,
								maxDepth
							  )
							: generated.children
					});

					// Log the final merged result
					const lastMerged = merged[merged.length - 1];
					processedExistingIndices.add(matchedIndex);
				} else {
					// No match found - add as new blank item
					merged.push(generated);
				}
			}

			// Phase 2: Add unmatched existing items if preserveAll is true
			if (preserveAll) {
				for (let i = 0; i < existingChildren.length; i++) {
					if (!processedExistingIndices.has(i)) {
						// This existing item didn't match any generated item
						// Keep it to preserve user's manual additions
						merged.push(existingChildren[i]);
					}
				}
			}


			return merged;
		// ===== END intelligent merging =====
		},

		updateItemColors(workingItem: LineItem, colorObj: ColorOption): void {
			if (this.locked) {
				this.showItemColorsByLine = null; // locked quote (#1475): nothing to save
				return;
			}
			console.log(workingItem, colorObj);

			if (
				workingItem.systemCategoryId == 14 ||
				this.$store.getters.requiresLettering(workingItem.systemCategoryId)
			) {
				workingItem.lettering = {
					letterPositionId: colorObj.letterPositionId,
					description: colorObj.description,
					text: colorObj.customText,
				};
			}

			// The menu emits colors and free texts in one array; keep the line item's
			// two arrays canonical or the print templates render each free text twice (#1431)
			const { colorSelections, freeTextSelections } = splitColorMenuSelections(
				colorObj.colorSelections
			);
			workingItem.colorSelections = colorSelections;
			this.$set(workingItem, "freeTextSelections", freeTextSelections);

			const upcharge = colorSelections.reduce((acc, colorSelection) => {
				return acc + (colorSelection?.upCharge || 0);
			}, 0);

			workingItem.colorUpCharges = 0;
			workingItem.colorUpCharges += upcharge;
			workingItem.matchPreviousPoNumber = colorObj.matchPreviousPoNumber;

			// Clear color validation error flag since user has provided data
			this.$set(workingItem, 'hasColorError', false);

			this.showItemColorsByLine = null; // Reset color dropdown state
			this.$emit("input", this.localTableData);
		},
		validateColors(workingItem: LineItem) {
			return hasValidColorSelections(
				workingItem,
				this.$store.getters.requiresLettering(workingItem.systemCategoryId)
			);
		},

		updateItemSizing(workingItem: LineItem, sizeData: any) {
			if (this.locked) {
				this.showItemSizingByLine = null; // locked quote (#1475): nothing to save
				return;
			}
			//update the workingItems sizing data
			workingItem.sizeSelections = sizeData.sizeSelections;
			workingItem.specialSizeSelections = sizeData.specialSizeSelections;
			workingItem.sizeUpCharge = sizeData.specialCharge;

			// Clear size validation error flag since user has provided data
			this.$set(workingItem, 'hasSizeError', false);

			//reset size dropdown state
			this.showItemSizingByLine = null;
			this.$emit("input", this.localTableData);
		},

		validateSizing(workingItem: LineItem) {
			return validateSizing(workingItem);
		},

		async getAnyInventoryItem(itemId: any) {
			let response = {};

			try {
				const res = await this.$apollo.query({
					query: GET_ANY_ITEM_BY_ID,
					variables: {
						id: itemId,
					},
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory item", res);
				return res.data.inventoryItem;
			} catch (error) {
				console.log(error);
			}

			return response;
		},

		async getInventoryItem(itemId: number): Promise<LineSubItem> {
			let response: LineSubItem = {} as LineSubItem;

			try {
				const res = await this.$apollo.query({
					query: gql`query {
				        inventoryItemById(id: ${itemId}) {
				            itemId: inventoryItemId
                            styleCode
                            description
                            vendorCost
                            price
                            systemCategoryId
                            dropShip
							stockItems
							processTypeId
                            phantomSubItems {
								priceIncluded
								subItemCategoryId: phantomSubItemId
                                parentItemId
								quantity
								isDefault
								braidPosition
                                itemData: subItemCategory {
                                    systemCategoryId
                                    name
                                    description
                                }
							}
                            subItems {
                                inventoryItemId: subItemId
                                parentItemId
                                quantity
                                isDefault
								priceIncluded
								braidPosition
                                itemData {
                                    systemCategoryId
                                    styleCode
                                    description
                                    dropShip
									stockItems
                                    price
                                    colorOptions {
                                        colorOptionId
                                        name
                                        description
                                        position
										type
                                        colorPalettes {
                                            name
                                            description
                                            upcharge
                                            colors {
                                                label
                                                description
                                                hexCode
                                                isVisible
                                                colorId
												isRetired
												isCustomColor
                                            }
                                        }
                                    }
                                }
                            }
                            colorOptions {
                                colorOptionId
                                name
                                description
                                position
								type
                                colorPalettes {
                                    name
                                    description
                                    upcharge
                                    colors {
                                        label
                                        description
                                        hexCode
                                        isVisible
                                        colorId
										isRetired
										isCustomColor
                                    }
                                }
                            }
                            sizeChart {
                                name
                                description
                                includeLengths
                                sizes {
                                    lengths {
										length
										sizeId
										sizeLengthId
										isRetired
									}
                                    name
                                    showSize
                                    sizeId
									isRetired
									sequence
                                }
                            }
				        }
				    }`,
					fetchPolicy: "no-cache",
				});
				console.log("getting inventory item", res);
				return res.data.inventoryItemById as LineSubItem;
			} catch (error) {
				console.log(error);
			}

			return response;
		},

		async getInventoryPackage(itemId: number) {
			let result: any = {
				// add default data here if needed
			};

			try {
				const res = await this.$apollo.query({
					query: gql`query {
				        packageById(id: ${itemId}) {
				            itemId: inventoryItemId
                            vendorId
							styleCode
							description
							vendorCost
							leadTime
							price
							systemCategoryId
							vendorPartNumber
							inactiveDate
							infiniteInventory
							dropShip
							packingList
							processTypeId
							stockItems
							createDate
							packageItems {
								id
								parentItemId
								packageCategoryId
								quantity
								isDefault
								priceIncluded
								packageId
								packageCategory {
									packageCategoryId
									name
									description
									systemCategoryId
									inventoryItemId
									inventoryItem {
										inventoryItemId
										systemCategoryId
										styleCode
										description
										type
									}
								}
								subItems {
									id
									parentItemId
									packageCategoryId
									packageCategory {
										packageCategoryId
										name
										description
										systemCategoryId
										inventoryItemId
										inventoryItem {
											inventoryItemId
											systemCategoryId
											styleCode
											description
											type
										}
									}
									quantity
									isDefault
									priceIncluded
									packageId
									inventoryItemId
									itemData: inventoryItem {
										inventoryItemId
										systemCategoryId
										styleCode
										description
										type
									}
									subItemCategoryId
									subItemCategory {
										name
										description
										systemCategoryId
									}
									braidPosition
								}
							}
				        }
				    }`,
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory package", res);
				return res.data.packageById;
			} catch (error) {
				console.log(error);
			}

			return result;
		},

		async getInventorySubItem(itemId: number): Promise<LineSubItem> {
			let result: LineSubItem = {} as LineSubItem;
			try {
				const res = await this.$apollo.query({
					query: gql`
						query GetSubItemById($id: Int!) {
							subItemById(id: $id) {
								itemId: inventoryItemId
								styleCode
								description
								vendorCost
								price
								systemCategoryId
								dropShip
								processTypeId
								colorOptions {
									colorOptionId
									name
									description
									position
									type
									colorPalettes {
										name
										description
										upcharge
										colors {
											label
											description
											hexCode
											isVisible
											colorId
											isRetired
											isCustomColor
										}
									}
								}
							}
						}
					`,
					fetchPolicy: "no-cache",
					variables: {
						id: itemId,
					},
				});

				console.log("getting inventory subItem", res);
				result = res.data.subItemById;
				return result;
			} catch (error) {
				console.log(error);
			}

			return result;
		},

		sizeSelections(item: LineItem): any {
			return item.sizeSelections as any;
		},

		formatNewLines(text: string) {
			if (!text) return "";
			return text.replace(/\n/g, "<br/>").replace(/\t/g, "&emsp;");
		},

		// ===== Item Selection Undo/Redo =====
		// Two-phase snapshot: capture on search field focus (before typing changes styleCode),
		// then commit to undo stack only when setItem actually replaces the item.

		/**
		 * Phase 1: Called on search field focus. Captures the root item's pristine state
		 * BEFORE the user starts typing (which overwrites styleCode via v-model).
		 */
		captureUndoSnapshot(parentIndex: number) {
			const rootItem = this.localTableData[parentIndex];
			if (!rootItem) return;

			this.pendingUndoSnapshot = {
				item: JSON.parse(JSON.stringify(rootItem)),
				parentIndex,
			};
		},

		/**
		 * Phase 2: Called from setItem. Commits the pending snapshot to the undo stack.
		 * If no pending snapshot exists (e.g., click-based selection), captures current state.
		 * @param newStyleCode - The styleCode of the item being selected (for toast message)
		 */
		commitUndoSnapshot(parentIndex: number, newStyleCode?: string) {
			let entry: any;
			if (this.pendingUndoSnapshot && this.pendingUndoSnapshot.parentIndex === parentIndex) {
				entry = this.pendingUndoSnapshot;
			} else {
				// Fallback: capture current state (may have stale styleCode from typing)
				const rootItem = this.localTableData[parentIndex];
				if (!rootItem) return;
				entry = {
					item: JSON.parse(JSON.stringify(rootItem)),
					parentIndex,
				};
			}
			// Store the new item's styleCode so undo toast can say "Undo: reverted <new> back to <old>"
			entry.newStyleCode = newStyleCode || '';
			this.itemUndoStack.push(entry);
			this.pendingUndoSnapshot = null;

			// Cap stack size
			if (this.itemUndoStack.length > 20) {
				this.itemUndoStack.shift();
			}

			// New action clears redo stack
			this.itemRedoStack = [];
		},

		/**
		 * Clears all database identity fields on an item and its descendants.
		 * After undo/redo, the restored snapshot has stale IDs from before the
		 * last save. The backend uses these to match existing DB rows — stale IDs
		 * cause concurrency or IDENTITY_INSERT errors. Clearing them forces the
		 * backend to treat everything as new inserts.
		 */
		clearLineItemIds(item: any) {
			if (!item) return;
			item.lineItemId = undefined;

			// Clear identity columns on owned entities (ColorSelection.Id, FreeTextSelection.Id)
			if (item.colorSelections) {
				item.colorSelections.forEach((cs: any) => { cs.id = undefined; });
			}
			if (item.freeTextSelections) {
				item.freeTextSelections.forEach((ft: any) => { ft.id = undefined; });
			}

			if (item.children && item.children.length > 0) {
				item.children.forEach((child: any) => this.clearLineItemIds(child));
			}
		},

		undoItemSelection() {
			if (this.itemUndoStack.length === 0) return;

			const snapshot = this.itemUndoStack.pop();
			const { parentIndex } = snapshot;

			// Snapshot current state at root level before restoring (for redo)
			const currentItem = this.localTableData[parentIndex];
			if (currentItem) {
				this.itemRedoStack.push({
					item: JSON.parse(JSON.stringify(currentItem)),
					parentIndex,
					newStyleCode: snapshot.newStyleCode,
				});
			}

			this.clearLineItemIds(snapshot.item);
			this.localTableData.splice(parentIndex, 1, snapshot.item);

			const undoneItem = snapshot.newStyleCode || 'selection';
			(this as any).$toast?.info(`Undo: reverted ${undoneItem}`, { timeout: 2000 });
		},

		redoItemSelection() {
			if (this.itemRedoStack.length === 0) return;

			const snapshot = this.itemRedoStack.pop();
			const { parentIndex } = snapshot;

			// Snapshot current state at root level before restoring (for undo)
			const currentItem = this.localTableData[parentIndex];
			if (currentItem) {
				this.itemUndoStack.push({
					item: JSON.parse(JSON.stringify(currentItem)),
					parentIndex,
					newStyleCode: snapshot.newStyleCode,
				});
			}

			this.clearLineItemIds(snapshot.item);
			this.localTableData.splice(parentIndex, 1, snapshot.item);

			const redoneItem = snapshot.newStyleCode || 'selection';
			(this as any).$toast?.info(`Redo: reapplied ${redoneItem}`, { timeout: 2000 });
		},
		// ===== END Item Selection Undo/Redo =====

		selectItem(index: number, currentItem: LineItem | LineSubItem) {
			if (this.locked) return; // locked quote (#1475)
			const item = this.filteredSearchResults[index];
			if (item) {
				this.setItem(
					currentItem,
					item,
					this.itemIndex,
					this.childIndex,
					this.subChildIndex,
					undefined,
					currentItem.lineItemId
				);
				this.preventCloseOnBlur = false;
			}
		},

		getVisibleSearchResults(currentItem: LineItem | LineSubItem) {
			// Start with filtered results (respects sample/non-sample filtering)
			let results = this.filteredSearchResults;

			// Further filter based on package item visibility logic
			if (
				currentItem.packageCategoryId &&
				currentItem.packageCategoryId > 0 &&
				!currentItem.upgradePackage
			) {
				// Only show package items when not in "All Items" mode
				return results.filter((res: any) => res.isPackageItem);
			}
			// Show all filtered items if upgradePackage is true or no package category
			return results;
		},

		keyboardShortcuts(
			item: LineItem | LineSubItem,
			e: any,
			itemIndex: number,
			childIndex: number | null = null,
			subChildIndex: number | null = null
		) {
			if (this.locked) return; // locked quote (#1475)
			const keys = { up: 38, down: 40, right: 39, tab: 9, enter: 13 };

			// Check if the keyCode is a navigation/action key
			if (!Object.values(keys).includes(e.keyCode)) {
				return;
			}

			this.itemIndex = itemIndex;
			this.childIndex = childIndex;
			this.subChildIndex = subChildIndex;

			const visibleResults = this.getVisibleSearchResults(item);

			// Auto-select if single visible result and Enter/Tab pressed
			if (
				(e.keyCode === keys.enter || e.keyCode === keys.tab) &&
				visibleResults.length === 1
			) {
			// ===== Check if Tab key would select the same item (no actual change) =====
				const selectedResult = visibleResults[0];
				const currentItemId = item.itemId;
				const selectedItemId = selectedResult.inventoryItemId;

				// If selecting the exact same item, prevent default and skip selection
				// This avoids unnecessary regeneration when user presses Tab without changing anything
				if (currentItemId === selectedItemId && currentItemId !== -1) {
				e.preventDefault();
					// Just move focus away from dropdown (blur)
					if (e.target && typeof e.target.blur === 'function') {
						e.target.blur();
					}
					return;
				}

				e.preventDefault();
				// Find the index in the full results array
				const fullIndex = this.filteredSearchResults.indexOf(visibleResults[0]);
				this.selectItem(fullIndex, item);
				return;
			}

			// Auto-select first result if Enter pressed with multiple results and no item focused
			if (
				e.keyCode === keys.enter &&
				visibleResults.length > 1 &&
				this.focusedItemIndex < 0
			) {
				e.preventDefault();
				// Select the first visible result
				const fullIndex = this.filteredSearchResults.indexOf(visibleResults[0]);
				this.selectItem(fullIndex, item);
				return;
			}

			if (e.keyCode === keys.up || e.keyCode === keys.down) {
				e.preventDefault();
				this.preventCloseOnBlur = true;
				this.moveFocus(e.keyCode === keys.up ? "up" : "down", item);
			} else if (e.keyCode === keys.right || e.keyCode === keys.tab || e.keyCode === keys.enter) {
				if (this.focusedItemIndex >= 0) {
					e.preventDefault();
					this.selectItem(this.focusedItemIndex, item);
				}
			}
		},

		moveFocus(direction: any, currentItem: LineItem | LineSubItem) {
			const visibleResults = this.getVisibleSearchResults(currentItem);
			const visibleLength = visibleResults.length;

			if (visibleLength === 0) return;

			// Get current position in visible results
			const currentVisibleIndex = visibleResults.findIndex(
				(res: any) => this.filteredSearchResults.indexOf(res) === this.focusedItemIndex
			);

			// Calculate new index in visible results
			let newVisibleIndex;
			if (direction === "up") {
				newVisibleIndex = currentVisibleIndex <= 0 ? visibleLength - 1 : currentVisibleIndex - 1;
			} else {
				newVisibleIndex = currentVisibleIndex >= visibleLength - 1 ? 0 : currentVisibleIndex + 1;
			}

			// Map back to the filtered results array index
			this.focusedItemIndex = this.filteredSearchResults.indexOf(visibleResults[newVisibleIndex]);

			// Wait for the DOM to update, then focus the new item
			this.$nextTick(() => {
				// Scope selection to only the currently active dropdown by using data-line attribute
				const activeDropdown = this.$el.querySelector(`[data-line="${this.showItemSearchByLine}"] .search-input-dropdown-results`);
				if (!activeDropdown) return;

				// Query only visible li elements (v-show doesn't remove from DOM, just hides them)
				let listItems = Array.from(activeDropdown.querySelectorAll("li")).filter(
					(li: HTMLElement) => li.offsetParent !== null
				);
				console.log(listItems);
				let newFocusedElement = listItems[newVisibleIndex] as HTMLElement | undefined;
				newFocusedElement && newFocusedElement.focus();
			});
		},

		flattenItems(items: any) {
			let result: any = [];
			items.forEach((item: any) => {
				result.push(item);
				if (item.children && item.children.length) {
					result = result.concat(this.flattenItems(item.children));
				}
			});
			return result;
		},

		quantityQuantifier(item: LineItem) {
			if (item.uniqueId && this.quantityState && this.quantityState.length > 0) {
				const quantityItem = this.quantityState.find((pi) => pi.uniqueId === item.uniqueId);

				// Use normalized quantity from quantityState (represents items per parent)
				// quantityState stores quantities normalized to parent quantity = 1
				if (quantityItem && quantityItem.quantity !== undefined) {
					return quantityItem.quantity;
				}
			}
			return 1;
		},

		/**
		 * Calculate the current base quantity for an item based on its parent and quantifier.
		 * This represents what the base quantity SHOULD be right now, regardless of stored values.
		 * Uses a proper fallback hierarchy to handle all edge cases.
		 *
		 * @param item - The child item to calculate base for
		 * @param parentItem - Optional parent item (for performance, avoids expensive search)
		 * @returns The calculated base quantity (quantifier × parent quantity)
		 */
		calculateCurrentBase(item: LineItem, parentItem?: LineItem): number {
			// Priority 1: Use stored baseQuantity if available (most reliable after page reload)
			// This is the value from the backend and represents the TRUE base at time of save
			if (item.baseQuantity !== undefined && item.baseQuantity !== null) {
				return item.baseQuantity;
			}

			// Priority 2: Use configured default quantity (packages, sub-items, etc.)
			// This ensures configured defaults are not treated as overrides
			if (item.packageItemQuantity !== undefined && item.packageItemQuantity !== null) {
				// Find parent to multiply by parent quantity
				let parent = parentItem;
				if (!parent) {
					const allItems = this.flattenItems(this.localTableData);
					parent = allItems.find((i: LineItem) => {
						return i.children && i.children.some((c: LineItem) => c.uniqueId === item.uniqueId);
					});
				}
				if (parent) {
					return Math.round(item.packageItemQuantity * parent.quantity);
				}
				// If no parent found, use package quantity as-is (assumes parent qty = 1)
				return item.packageItemQuantity;
			}

			// Priority 3: Calculate from parent if available (for newly added items or dynamic updates)
			let parent = parentItem;

			// If no parent provided, find it
			if (!parent) {
				const allItems = this.flattenItems(this.localTableData);
				parent = allItems.find((i: LineItem) => {
					return i.children && i.children.some((c: LineItem) => c.uniqueId === item.uniqueId);
				});
			}

			// If parent found, calculate base from first principles
			if (parent) {
				const quantifier = this.quantityQuantifier(item);
				return Math.round(quantifier * parent.quantity);
			}

			// Priority 4: If item has override, derive base from current quantity
			if (item.isManuallyOverridden &&
				item.quantityOverride !== undefined &&
				item.quantityOverride !== null) {
				return item.quantity - item.quantityOverride;
			}

			// Final fallback: Use quantifier as base (assumes parent qty = 1)
			return this.quantityQuantifier(item);
		},

		/**
		 * Get the minimum allowed quantity for an item input field.
		 * Always returns 1 to allow sub-items to have lower quantity than their parent.
		 * For example, 4 out of 5 jackets getting a specific decoration.
		 *
		 * @param item - The item to get min quantity for
		 * @param parentItem - The parent item (unused, kept for API compatibility)
		 * @returns Minimum quantity (always 1)
		 */
		getMinQuantity(_item: LineItem, _parentItem?: LineItem): number {
			return 1;
		},

		/**
		 * Automatically handle quantity overrides when user changes sub-item quantity.
		 * Allows users to increase above base (creates override) or reduce back to base (clears override).
		 *
		 * @param item - The sub-item being edited
		 * @param parentItem - The parent item (provides context for base calculation)
		 */
		handleAutoOverrideQuantityChange(item: LineItem, parentItem: LineItem) {
			if (this.locked) return; // locked quote (#1475)
			// Calculate what the base SHOULD be right now
			const calculatedBase = this.calculateCurrentBase(item, parentItem);

			// Store the calculated base (updates stored value to current reality)
			this.$set(item, 'baseQuantity', calculatedBase);

			// Ensure quantity is at least 1 (prevent zero/negative)
			if (item.quantity < 1) {
				this.$set(item, 'quantity', 1);
				if (calculatedBase > 1) {
					// Qty 1 is below base - treat as downward override
					this.$set(item, 'isManuallyOverridden', true);
					this.$set(item, 'quantityOverride', 1 - calculatedBase); // negative
					this.$set(item, 'extraItemsPrice', 0);
				}
				return;
			}

			// Case 1: Quantity is greater than base - enable override
			if (item.quantity > calculatedBase) {
				const override = item.quantity - calculatedBase;
				this.$set(item, 'isManuallyOverridden', true);
				this.$set(item, 'quantityOverride', override);

				// Calculate price for extra items
				if (item.price && override > 0) {
					this.$set(item, 'extraItemsPrice', item.price * override);
				} else {
					this.$set(item, 'extraItemsPrice', 0);
				}
			}
			// Case 2: Quantity equals base - clear override (back to auto-calculated)
			else if (item.quantity === calculatedBase) {
				this.$set(item, 'isManuallyOverridden', false);
				this.$set(item, 'quantityOverride', 0);
				this.$set(item, 'extraItemsPrice', 0);
			}
			// Case 3: Quantity is less than base - allow downward override
			// e.g., only 4 of 5 jackets getting a specific decoration
			else {
				this.$set(item, 'isManuallyOverridden', true);
				this.$set(item, 'quantityOverride', item.quantity - calculatedBase); // negative
				this.$set(item, 'extraItemsPrice', 0); // no extra charge for fewer items
			}

			// Update any grandchildren if this item has them
			if (item.children && item.children.length > 0) {
				this.updateItemQuantities(item, parentItem);
			}
		},

		/**
		 * Calculate the base (auto-calculated) quantity for an item.
		 * This method finds the parent and delegates to calculateCurrentBase().
		 *
		 * @param item - The item to calculate base for
		 * @returns The calculated base quantity
		 */
		calculateBaseQuantity(item: LineItem): number {
			// Find parent item in the flattened items list
			const allItems = this.flattenItems(this.localTableData);
			const parent = allItems.find((i: LineItem) => {
				return i.children && i.children.some((c: LineItem) => c.uniqueId === item.uniqueId);
			});

			// Delegate to the new calculation method with parent context
			return this.calculateCurrentBase(item, parent);
		},

		// Initialize override states for items loaded from backend
		initializeOverrideStates(items: LineItem[]) {
			// First ensure unique IDs are set
			this.addUniqueIdsToItems(items);

			items.forEach((item) => {
				// Process children of main items
				if (item.children && item.children.length > 0) {
					item.children.forEach((child) => {
						// Ensure child has a unique ID
						if (!child.uniqueId) {
							this.$set(child, 'uniqueId', this.generateUniqueId());
						}

						// Check if saved data has override fields from backend
						if (child.isManuallyOverridden === true &&
							child.baseQuantity !== undefined && child.baseQuantity !== null &&
							child.quantityOverride !== undefined && child.quantityOverride !== null) {
							// Data already has override info from backend - ensure it's properly set
							this.$set(child, 'isManuallyOverridden', true);
							this.$set(child, 'baseQuantity', child.baseQuantity);
							this.$set(child, 'quantityOverride', child.quantityOverride);

							// Calculate extraItemsPrice for display and totals
							if (child.price && child.quantityOverride > 0) {
								this.$set(child, 'extraItemsPrice', child.price * child.quantityOverride);
							} else {
								this.$set(child, 'extraItemsPrice', 0);
							}
						} else {
							// Legacy data or data without override fields - detect if quantity has been overridden
							// Calculate what base should be based on parent and quantifier
							const calculatedBase = this.calculateCurrentBase(child, item);

							if (child.quantity && child.quantity > calculatedBase) {
								// Quantity is higher than expected - this is an override
								const override = child.quantity - calculatedBase;
								this.$set(child, 'isManuallyOverridden', true);
								this.$set(child, 'baseQuantity', calculatedBase);
								this.$set(child, 'quantityOverride', override);

								// Calculate extraItemsPrice for override
								if (child.price && override > 0) {
									this.$set(child, 'extraItemsPrice', child.price * override);
								} else {
									this.$set(child, 'extraItemsPrice', 0);
								}
							} else {
								// No override detected - standard auto-calculated quantity
								this.$set(child, 'isManuallyOverridden', false);
								this.$set(child, 'baseQuantity', calculatedBase);
								this.$set(child, 'quantityOverride', 0);
								this.$set(child, 'extraItemsPrice', 0);

								// Ensure quantity matches base for non-overridden items
								if (child.quantity !== calculatedBase) {
									this.$set(child, 'quantity', calculatedBase);
								}
							}
						}

						// Process grandchildren (sub-items of children)
						if (child.children && child.children.length > 0) {
							child.children.forEach((grandchild) => {
								// Ensure grandchild has a unique ID
								if (!grandchild.uniqueId) {
									this.$set(grandchild, 'uniqueId', this.generateUniqueId());
								}

								if (grandchild.isManuallyOverridden === true &&
									grandchild.baseQuantity !== undefined && grandchild.baseQuantity !== null &&
									grandchild.quantityOverride !== undefined && grandchild.quantityOverride !== null) {
									// Data already has override info from backend
									this.$set(grandchild, 'isManuallyOverridden', true);
									this.$set(grandchild, 'baseQuantity', grandchild.baseQuantity);
									this.$set(grandchild, 'quantityOverride', grandchild.quantityOverride);

									// Calculate extraItemsPrice for display and totals
									if (grandchild.price && grandchild.quantityOverride > 0) {
										this.$set(grandchild, 'extraItemsPrice', grandchild.price * grandchild.quantityOverride);
									} else {
										this.$set(grandchild, 'extraItemsPrice', 0);
									}
								} else {
									// Legacy data for grandchildren
									const grandchildBase = this.calculateCurrentBase(grandchild, child);

									if (grandchild.quantity && grandchild.quantity > grandchildBase) {
										const grandchildOverride = grandchild.quantity - grandchildBase;
										this.$set(grandchild, 'isManuallyOverridden', true);
										this.$set(grandchild, 'baseQuantity', grandchildBase);
										this.$set(grandchild, 'quantityOverride', grandchildOverride);

										// Calculate extraItemsPrice for override
										if (grandchild.price && grandchildOverride > 0) {
											this.$set(grandchild, 'extraItemsPrice', grandchild.price * grandchildOverride);
										} else {
											this.$set(grandchild, 'extraItemsPrice', 0);
										}
									} else {
										this.$set(grandchild, 'isManuallyOverridden', false);
										this.$set(grandchild, 'baseQuantity', grandchildBase);
										this.$set(grandchild, 'quantityOverride', 0);
										this.$set(grandchild, 'extraItemsPrice', 0);

										// Ensure quantity matches base for non-overridden items
										if (grandchild.quantity !== grandchildBase) {
											this.$set(grandchild, 'quantity', grandchildBase);
										}
									}
								}
							});
						}
					});
				}
			});
		},

		generateUniqueId() {
			return `customId-${this.uniqueIdCounter++}`;
		},

		addUniqueIdsToItems(items: any) {
			items.forEach((item: any) => {
				if (!item.uniqueId) {
					item.uniqueId = this.generateUniqueId();
				}

				// Recursively assign unique IDs to children if they exist
				if (item.children && item.children.length > 0) {
					this.addUniqueIdsToItems(item.children);
				}
			});
		},

		adjustChildrenQuantities(item: any, adjustmentFactor: any) {
			if (item.children && item.children.length > 0) {
				item.children.forEach((child) => {
					// Use baseQuantity if available (excludes overrides from normalization)
					// This ensures quantifier reflects the true items-per-parent ratio
					const quantityToNormalize = (child.baseQuantity !== undefined && child.baseQuantity !== null)
						? child.baseQuantity
						: child.quantity;

					// CRITICAL: Save original TOTAL quantity before normalization
					// Grandchildren's baseQuantity is calculated from parent's TOTAL quantity (including overrides)
					const originalTotalQuantity = child.quantity;

					child.quantity = quantityToNormalize * adjustmentFactor;

					// Store baseQuantity for quantifier calculation
					child.baseQuantity = child.quantity;

					if (child.children && child.children.length > 0) {
						// Calculate new adjustmentFactor based on parent's original TOTAL quantity
						// This ensures grandchildren are normalized relative to their direct parent
						const childAdjustmentFactor = 1 / (originalTotalQuantity > 0 ? originalTotalQuantity : 1);
						this.adjustChildrenQuantities(child, childAdjustmentFactor);
					}
				});
			}
		},

		handleScrollClose(e: Event) {
			// Allow scrolling inside the dropdown results list
			const target = e.target as HTMLElement;
			if (target && target.closest && target.closest('.search-input-dropdown')) {
				return;
			}

			// Throttle repositioning via requestAnimationFrame
			if ((this as any)._scrollRafId) return;
			(this as any)._scrollRafId = requestAnimationFrame(() => {
				(this as any)._scrollRafId = null;
				if (this.showItemSearchByLine === null) return;

				const activeInput = document.querySelector(
					`.search-input[data-line="${this.showItemSearchByLine}"]`
				);
				if (!activeInput) {
					this.showItemSearchByLine = null;
					return;
				}

				const rect = activeInput.getBoundingClientRect();
				const inputCenter = rect.top + rect.height / 2;

				// Fast scroll detection: if input moved far since last frame, close
				const lastTop = (this as any)._dropdownInputLastTop;
				const delta = lastTop != null ? Math.abs(rect.top - lastTop) : 0;
				(this as any)._dropdownInputLastTop = rect.top;

				if (delta > 80) {
					this.showItemSearchByLine = null;
					return;
				}

				// Off-screen detection: if input center is outside viewport, close
				if (inputCenter < 0 || inputCenter > window.innerHeight) {
					this.showItemSearchByLine = null;
					return;
				}

				// Still visible, slow scroll — reposition dropdown to follow
				this.positionDropdown();
			});
		},

		positionDropdown() {
			if (this.showItemSearchByLine !== null) {
				this.$nextTick(() => {
					const activeInput = document.querySelector(
						`.search-input[data-line="${this.showItemSearchByLine}"]`
					);
					const activeDropdown = document.querySelector(
						`.search-input-dropdown[data-line="${this.showItemSearchByLine}"]`
					) as HTMLElement;

					if (activeInput && activeDropdown) {
						const rect = activeInput.getBoundingClientRect();
						const dropdownHeight = activeDropdown.offsetHeight;

						// Check if there's room below
						const spaceBelow = window.innerHeight - rect.bottom;
						const spaceAbove = rect.top;

						if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
							// Position below
							activeDropdown.style.top = `${rect.bottom}px`;
							activeDropdown.style.left = `${rect.left}px`;
							// activeDropdown.style.width = `${rect.width}px`;
						} else {
							// Position above
							activeDropdown.style.top = `${rect.top - dropdownHeight}px`;
							activeDropdown.style.left = `${rect.left}px`;
							// activeDropdown.style.width = `${rect.width}px`;
						}

						activeDropdown.classList.add("dropdown-active");
					}
				});
			}
		},

		getInitials(text: string) {
			return text
				.replace(/\(.*?\)/g, "") // Remove content within parentheses
				.replace(/[[\]{}]/g, "") // Removed unnecessary escapes
				.replace(/[^\w\s-]/g, "") // Remove other special characters except hyphens
				.trim()
				.split(/[\s-]+/) // Split on spaces and hyphens
				.map((word) => word.charAt(0).toUpperCase())
				.join("");
		},

		checkForOverflow() {
			(this as any).$nextTick(() => {
				const wrapper = (this as any).$el.querySelector(".sales-table-body-wrapper");
				if (wrapper) {
					(this as any).hasOverflow = wrapper.scrollHeight > wrapper.clientHeight;
				}
			});
		},
		scrollToItem(lineNumber: number) {
			(this as any).$nextTick(() => {
				const wrapper = (this as any).$el.querySelector(".sales-table-body-wrapper");
				const element = (this as any).$el.querySelector(`[data-row-line="${lineNumber}"]`);
				console.log(element);
				if (wrapper && element) {
					const wrapperRect = wrapper.getBoundingClientRect();
					const elementRect = element.getBoundingClientRect();

					// Calculate if element is outside visible area
					const isBelow = elementRect.bottom > wrapperRect.bottom;
					const isAbove = elementRect.top < wrapperRect.top;

					if (isBelow || isAbove) {
						wrapper.scrollTo({
							top: element.offsetTop - wrapper.offsetTop - 20, // 20px padding from top
							behavior: "smooth",
						});
					}
				}
			});
		},
	},
	computed: {
		computedItemsTable() {
			let itemsArr: LineItem[] = [];

			if (this.localTableData && this.localTableData.length > 0) {
				this.localTableData.forEach((item: LineItem) => {
					if (!itemsArr.some((existingItem) => existingItem.uniqueId === item.uniqueId)) {
						itemsArr.push(item);
					}

					if (item.children && item.children.length > 0) {
						item.children.forEach((childItem: LineItem) => {
							if (!itemsArr.some((existingItem) => existingItem.uniqueId === childItem.uniqueId)) {
								itemsArr.push(childItem);
							}

							if (childItem.children && childItem.children.length > 0) {
								childItem.children.forEach((sub: LineItem) => {
									if (!itemsArr.some((existingItem) => existingItem.uniqueId === sub.uniqueId)) {
										itemsArr.push(sub);
									}
								});
							}
						});
					}
				});
			}

			return itemsArr;
		},
		packageItems() {
			let packageItemsSet = new Set();
			let distinctPackageItems = [] as any[];

			if (this.localTableData && this.localTableData.length > 0) {
				this.localTableData.forEach((item) => {
					if (item.packageItems && item.packageItems.length > 0) {
						item.packageItems.forEach((packageItem) => {
							// Convert the packageItem object into a string to use in a Set for uniqueness
							const packageItemString = JSON.stringify(packageItem);

							// If the package item hasn't been added to the set, add it
							if (!packageItemsSet.has(packageItemString)) {
								packageItemsSet.add(packageItemString);
								distinctPackageItems.push(packageItem);
							}
						});
					} else if (item.children && item.children.length > 0) {
						item.children.forEach((packageItem) => {
							// Convert the packageItem object into a string to use in a Set for uniqueness
							const packageItemString = JSON.stringify(packageItem);

							// If the package item hasn't been added to the set, add it
							if (!packageItemsSet.has(packageItemString)) {
								packageItemsSet.add(packageItemString);
								distinctPackageItems.push(packageItem);
							}
						});
					}
				});
			}

			return distinctPackageItems;
		},
		tableBodyStyle() {
			const headerHeight = document.getElementById("sales-table-header")?.offsetHeight || 0;
			const footerHeight = document.getElementById("sales-table-footer")?.offsetHeight || 0;
			const availableHeight = this.maxHeight - headerHeight - footerHeight;

			if (this.expanded) {
				// When expanded, use the content's full height
				return {
					height: this.$refs.content ? `${this.$refs.content.scrollHeight}px` : "auto",
				};
			} else {
				// When collapsed, use the specified max height
				return {
					height: `${Math.max(availableHeight, 200)}px`,
				};
			}
		},
		isQuotesRoute() {
			return this.$route.path.startsWith("/sales-orders/quotes");
		},
		/**
		 * Checks if the current order contains any sample items.
		 * Uses the $systemCategories plugin to determine if items belong to the "Sample" category.
		 */
		hasSampleItems(): boolean {
			if (!this.localTableData || this.localTableData.length === 0) return false;
			return this.localTableData.some((item: any) => {
				// Check top-level item
				if (item.itemId && item.itemId > 0 && this.$systemCategories.isSample(item.systemCategoryId)) {
					return true;
				}
				// Check children
				if (item.children && item.children.length > 0) {
					return item.children.some((child: any) => {
						if (child.itemId && child.itemId > 0 && this.$systemCategories.isSample(child.systemCategoryId)) {
							return true;
						}
						// Check grandchildren
						if (child.children && child.children.length > 0) {
							return child.children.some((grandChild: any) =>
								grandChild.itemId && grandChild.itemId > 0 && this.$systemCategories.isSample(grandChild.systemCategoryId)
							);
						}
						return false;
					});
				}
				return false;
			});
		},
		/**
		 * Checks if the current order contains any non-sample (regular) items.
		 * This excludes placeholder items (itemId <= 0) and sub-items.
		 */
		hasNonSampleItems(): boolean {
			if (!this.localTableData || this.localTableData.length === 0) return false;
			return this.localTableData.some((item: any) => {
				// Only check top-level items that are actual inventory items (not sub-items, not placeholders)
				if (item.itemId && item.itemId > 0 &&
					!this.$systemCategories.isSubItem(item.systemCategoryId) &&
					!this.$systemCategories.isSample(item.systemCategoryId)) {
					return true;
				}
				return false;
			});
		},
		/**
		 * Filters search results to exclude items that would create a mixed sample/non-sample order.
		 * If order has sample items, hide non-sample items and vice versa.
		 */
		filteredSearchResults(): any[] {
			if (!this.itemSearchResults || this.itemSearchResults.length === 0) {
				return [];
			}

			// If no items yet, show all results
			if (!this.hasSampleItems && !this.hasNonSampleItems) {
				return this.itemSearchResults;
			}

			return this.itemSearchResults.filter((res: any) => {
				const isSampleItem = this.$systemCategories.isSample(res.systemCategoryId);
				const isSubItem = this.$systemCategories.isSubItem(res.systemCategoryId);

				// Always allow sub-items (they can be attached to either type)
				if (isSubItem) {
					return true;
				}

				// If order has sample items, only show sample items
				if (this.hasSampleItems) {
					return isSampleItem;
				}

				// If order has non-sample items, only show non-sample items
				if (this.hasNonSampleItems) {
					return !isSampleItem;
				}

				return true;
			});
		},
		console: () => console,
		window: () => window,
	},
	watch: {
		value: {
			handler(vv, old) {
				// Always sync localTableData when value changes from parent
				// Use deep comparison to avoid infinite loops
				if (JSON.stringify(vv) !== JSON.stringify(this.localTableData)) {
					this.localTableData = vv;
					// Initialize override states when data changes
					if (this.localTableData && this.localTableData.length > 0) {
						this.initializeOverrideStates(this.localTableData);
					}
				}
			},
			deep: true,
		},
		localTableData: {
			handler(newVal) {
				this.$emit("input", this.localTableData);
				this.addUniqueIdsToItems(this.localTableData);

				// Adjust quantities to get base quantity ratio for adjusting package quantities
				let stateArray = [];
				newVal.forEach((rootItem) => {
					const cloneItem = JSON.parse(JSON.stringify(rootItem));
					const adjustmentFactor = 1 / cloneItem.quantity;
					this.adjustChildrenQuantities(cloneItem, adjustmentFactor);
					stateArray.push(cloneItem);
				});

				const flattenedItems = this.flattenItems(stateArray);
				flattenedItems.forEach((item) => {
					if (!this.quantityState.find((qs) => qs.uniqueId === item?.uniqueId)) {
						this.quantityState.push(JSON.parse(JSON.stringify(item)));
					}
				});

				this.checkForOverflow();
			},
			deep: true,
		},
		showItemSearchByLine(newVal) {
			if (newVal !== null) {
				this.positionDropdown();
				// Initialize scroll tracking for smart close behavior
				const activeInput = document.querySelector(`.search-input[data-line="${newVal}"]`);
				(this as any)._dropdownInputLastTop = activeInput ? activeInput.getBoundingClientRect().top : null;
				window.addEventListener('scroll', this.handleScrollClose, true);
			} else {
				window.removeEventListener('scroll', this.handleScrollClose, true);
				(this as any)._dropdownInputLastTop = null;
				if ((this as any)._scrollRafId) {
					cancelAnimationFrame((this as any)._scrollRafId);
					(this as any)._scrollRafId = null;
				}
			}
		},
		maxHeight() {
			this.checkForOverflow();
		},
		expanded(newVal) {
			// Force a reflow to ensure smooth transition
			if (this.$refs.wrapper) {
				this.$refs.wrapper.style.height = newVal
					? `${this.$refs.content.scrollHeight}px`
					: `${this.maxHeight}px`;
			}
		},
	},
	mounted() {
		this.localTableData = this.value as LineItem[];

		// Initialize override states for loaded data
		if (this.localTableData && this.localTableData.length > 0) {
			this.initializeOverrideStates(this.localTableData);
		}

		this.checkForOverflow();
		const resizeObserver = new ResizeObserver(() => {
			this.checkForOverflow();
		});

		const wrapper = this.$el.querySelector(".sales-table-body-wrapper");
		if (wrapper) {
			resizeObserver.observe(wrapper);
		}

		// Store observer for cleanup
		this.resizeObserver = resizeObserver;

		this.$nextTick(() => {
			if (this.isQuotesRoute && (!this.value || this.value.length === 0)) {
				this.addMainItem();
			}
		});

		// Ctrl+Z / Ctrl+Y for item selection undo/redo.
		// Always intercepts when undo/redo stack has items, even inside text inputs,
		// so users can undo an item selection immediately after focus moves to quantity.
		this.handleUndoRedo = (e: KeyboardEvent) => {
			if (this.locked) return; // locked quote (#1475)
			if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
				if (this.itemUndoStack.length > 0) {
					e.preventDefault();
					this.undoItemSelection();
				}
			} else if (
				((e.ctrlKey || e.metaKey) && e.key === 'y') ||
				((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'z' || e.key === 'Z'))
			) {
				if (this.itemRedoStack.length > 0) {
					e.preventDefault();
					this.redoItemSelection();
				}
			}
		};
		document.addEventListener('keydown', this.handleUndoRedo);
	},
	beforeDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.handleUndoRedo) {
			document.removeEventListener('keydown', this.handleUndoRedo);
		}
		window.removeEventListener('scroll', this.handleScrollClose, true);
		if ((this as any)._scrollRafId) {
			cancelAnimationFrame((this as any)._scrollRafId);
		}
	},
};
